import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorHandler } from "@/core/api/api-error-handler";
import {
  createPreviewActions,
  ShareablePreview,
} from "@/core/components/shared/preview-action";
import { Label } from "@/core/components/ui/label";
import { Textarea } from "@/core/components/ui/textarea";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import type { TransactionDto } from "@/features/wallet/api/dto";
import { useOpenTransactionDispute } from "@/features/wallet/api/use-open-transaction-dispute";
import { createOpenTransactionDisputeSchema } from "@/features/wallet/schemas/dispute.schema";

/**
 * Ouverture d'une contestation par l'expéditeur (`POST
 * /TransactionDisputes/open`, guide v3.1) — transfert COMPLETED entre
 * portefeuilles, au plus tard 1 h après l'envoi. Le montant reçu est gelé
 * chez le bénéficiaire dans la limite de son disponible ; le motif (5–500
 * caractères) ne lui est jamais transmis.
 *
 * Preview `ShareablePreview` obligatoire (règle projet) : récap transaction
 * (IDs et montants en `NoTranslate` via la preview) + motif obligatoire
 * avant l'appel API idempotent.
 */
export function OpenTransactionDisputeDialog({
  transaction,
  open,
  onOpenChange,
}: {
  transaction: TransactionDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTypedTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : schéma zod traduit reconstruit au changement de langue.
  const schema = useMemo(() => createOpenTransactionDisputeSchema(), [i18n.language]);

  const openDispute = useOpenTransactionDispute(transaction.id);
  const pending = openDispute.isPending;
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | undefined>();

  const close = () => {
    if (pending) return;
    setReason("");
    setReasonError(undefined);
    onOpenChange(false);
  };

  const handleConfirm = () => {
    const parsed = schema.safeParse({ reason });
    if (!parsed.success) {
      setReasonError(parsed.error.issues[0]?.message);
      return;
    }
    openDispute.mutate(
      { transactionId: transaction.id, reason: parsed.data.reason },
      {
        onSuccess: () => {
          notifSilence(t("wallet.disputes.feedback.opened"));
          setReason("");
          setReasonError(undefined);
          onOpenChange(false);
        },
        onError: (error) =>
          notifSilenceError(apiErrorHandler(error) || error.message),
      }
    );
  };

  if (!open) return null;

  return (
    <ShareablePreview
      data={{
        transactionId: transaction.id,
        reference: transaction.reference,
        amountMinor: transaction.amountMinor,
        currency: transaction.currency,
      }}
      title={t("wallet.disputes.open.title")}
      description={t("wallet.disputes.open.description")}
      mode="user"
      defaultOpen
      onOpenChange={(isOpen) => {
        if (!isOpen) close();
      }}
      maxHeight="90vh"
      customView={() => (
        <div className="space-y-1.5">
          <Label htmlFor="dispute-reason">
            {t("wallet.disputes.open.reason")}
          </Label>
          <Textarea
            id="dispute-reason"
            maxLength={500}
            placeholder={t("wallet.disputes.open.reasonPlaceholder")}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (reasonError) setReasonError(undefined);
            }}
          />
          {reasonError ? (
            <p className="text-sm text-destructive">{reasonError}</p>
          ) : null}
        </div>
      )}
      actions={createPreviewActions({
        onConfirm: handleConfirm,
        onCancel: close,
        t: (key: string) => String(t(key as TFunctionType)),
        labels: { confirm: t("wallet.disputes.open.confirm") },
        confirmLoading: pending,
        withIndicatorOnConfirm: true,
      })}
    />
  );
}
